import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../api/db';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/layout/Sidebar';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiExternalLink, FiTag } = FiIcons;

const emptyForm = { name: '', url: '', description: '', tags: '' };

const Links = () => {
  const { isAuthenticated, user, isSuperuser } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, link: null, confirmText: '' });
  const [deleting, setDeleting] = useState(false);

  const loadLinks = async () => {
    setLoading(true);
    try {
      const data = await db.getLinks();
      setLinks(data);
    } catch (e) {
      console.error('Failed to load links', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLinks();
  }, []);

  const filteredLinks = useMemo(() => {
    if (!searchQuery.trim()) return links;
    const q = searchQuery.toLowerCase();
    return links.filter(link =>
      link.name.toLowerCase().includes(q) ||
      link.description.toLowerCase().includes(q) ||
      link.tags.toLowerCase().includes(q) ||
      link.url.toLowerCase().includes(q)
    );
  }, [links, searchQuery]);

  const canModify = (link) => {
    if (!isAuthenticated || !user) return false;
    if (isSuperuser) return true;
    return link.author_email === user.email;
  };

  const openAdd = () => {
    setEditingLink(null);
    setForm(emptyForm);
    setFormError('');
    setIsModalOpen(true);
  };

  const openEdit = (link) => {
    setEditingLink(link);
    setForm({
      name: link.name || '',
      url: link.url || '',
      description: link.description || '',
      tags: link.tags || ''
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingLink(null);
    setForm(emptyForm);
    setFormError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name) { setFormError('Please enter a link name.'); return; }
    if (!url) { setFormError('Please enter a URL.'); return; }
    if (!/^https?:\/\//i.test(url)) { setFormError('URL must start with http:// or https://'); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        url,
        description: form.description.trim(),
        tags: form.tags.trim()
      };
      if (editingLink) payload.id = editingLink.id;
      await db.saveLink(payload);
      await loadLinks();
      closeModal();
    } catch (err) {
      setFormError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (link) => {
    setDeleteModal({ open: true, link, confirmText: '' });
  };

  const closeDelete = () => {
    setDeleteModal({ open: false, link: null, confirmText: '' });
  };

  const handleDelete = async () => {
    const { link, confirmText } = deleteModal;
    if (!link || confirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await db.deleteLink(link.id);
      await loadLinks();
      closeDelete();
    } catch (err) {
      alert(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const tagList = (tags) => {
    if (!tags) return [];
    return tags.split(',').map(t => t.trim()).filter(Boolean);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="mb-8">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">Useful links</h1>
          <p className="text-xl text-gray-600 max-w-2xl mb-6">
            Quick access to helpful resources and tools for our community.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            <div className="relative flex-1 max-w-xl">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search links by name, tag or description..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] bg-white"
              />
            </div>
            {isAuthenticated && (
              <button
                onClick={openAdd}
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-medium transition-colors hover:opacity-90"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                <SafeIcon icon={FiPlus} /> Add link
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Main Content */}
          <div className="lg:w-3/4 min-h-[400px]">
            {loading ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
                <p className="text-gray-500">Loading links...</p>
              </div>
            ) : filteredLinks.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
                <p className="text-gray-500 text-lg">
                  {searchQuery ? 'No links match your search.' : 'No links yet.'}
                </p>
                {isAuthenticated && !searchQuery && (
                  <button
                    onClick={openAdd}
                    className="mt-4 font-medium hover:underline"
                    style={{ color: 'var(--brand-primary)' }}
                  >
                    Add the first link
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredLinks.map(link => (
                  <div
                    key={link.id}
                    className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="text-lg font-bold text-gray-900 line-clamp-2 leading-tight">
                        {link.name}
                      </h3>
                      {canModify(link) && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => openEdit(link)}
                            className="text-gray-400 hover:text-[var(--brand-primary)] p-1.5 rounded-md hover:bg-gray-50 transition-colors"
                            title="Edit"
                          >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDelete(link)}
                            className="text-gray-400 hover:text-red-500 p-1.5 rounded-md hover:bg-gray-50 transition-colors"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {link.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-1">
                        {link.description}
                      </p>
                    )}

                    {link.tags && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {tagList(link.tags).map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: 'var(--brand-primary-light)', color: 'var(--brand-primary)' }}
                          >
                            <SafeIcon icon={FiTag} className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-auto inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors hover:opacity-90"
                      style={{ backgroundColor: 'var(--brand-primary)' }}
                    >
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                      Open link
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/4">
            <div className="sticky top-28">
              <Sidebar />
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{editingLink ? 'Edit Link' : 'Add Link'}</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  placeholder="e.g. Haringey Council Jobs"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input
                  type="url"
                  value={form.url}
                  onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  placeholder="jobs, council, careers (comma separated)"
                />
                <p className="text-xs text-gray-500 mt-1">Separate tags with commas.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quick description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  placeholder="Short description of the link..."
                />
              </div>
              {formError && <p className="text-sm text-red-600">{formError}</p>}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg font-medium disabled:opacity-75"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  <SafeIcon icon={FiSave} /> {saving ? 'Saving...' : editingLink ? 'Update' : 'Add Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete link?</h2>
              <p className="text-sm text-gray-600 mb-4">
                This will permanently remove <strong>{deleteModal.link?.name}</strong>. Type <strong>DELETE</strong> below to confirm.
              </p>
              <input
                type="text"
                value={deleteModal.confirmText}
                onChange={e => setDeleteModal(m => ({ ...m, confirmText: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
                placeholder="Type DELETE to confirm"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeDelete}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteModal.confirmText !== 'DELETE' || deleting}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg font-medium disabled:opacity-60"
                  style={{ backgroundColor: '#ef4444' }}
                >
                  <SafeIcon icon={FiTrash2} /> {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Links;
